import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Space, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useGenerateSasUrlMutation } from '@/store/apis/documentsApi';
import { TaxDocumentRecord } from '@/store/apis/taxDocumentsApi';
import {
  DocumentFrame,
  downloadFromUrl,
  VIEWER_BACKDROP,
} from '@/components/DocumentViewer';

const { Text } = Typography;

interface TaxRightPanelProps {
  document: TaxDocumentRecord;
  open: boolean;
  onClose: () => void;
}

/**
 * A tax document in a right-hand drawer, the same shape the documents checker
 * uses - see `CoverageDocumentDrawer`.
 *
 * A drawer rather than the panel that used to open inside the row: the tree is
 * dense and deeply indented, so an inline viewer pushed everything below it
 * down the page and left the document itself in a 520px letterbox.
 */
const TaxRightPanel: React.FC<TaxRightPanelProps> = ({
  // Renamed off the prop: `document` shadows the global, which is how the old
  // tax preview ended up writing `window.document.createElement`.
  document: doc,
  open,
  onClose,
}) => {
  const [sasUrl, setSasUrl] = useState<string | null>(null);
  const [generateSasUrl, { isLoading }] = useGenerateSasUrlMutation();

  const fileName = doc.copied?.newName ?? doc.drive?.name ?? '';

  /* The blob fields, not the document object: `doc` is a new reference on every
   * parent render, and depending on it re-minted a SAS URL each time rather
   * than once per document. */
  const container = doc.blob?.container;
  const blobName = doc.blob?.blobName;

  useEffect(() => {
    if (!container || !blobName) {
      setSasUrl(null);
      return;
    }

    let current = true;
    generateSasUrl({ containerName: container, blobName })
      .unwrap()
      .then((res) => {
        if (current) setSasUrl(res?.data?.url ?? null);
      })
      .catch(() => {
        if (current) setSasUrl(null);
      });

    return () => {
      current = false;
    };
  }, [container, blobName, generateSasUrl]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      title={
        <Text
          ellipsis={{ tooltip: fileName }}
          style={{ maxWidth: 420, fontSize: 13 }}
        >
          {fileName || 'Document preview'}
        </Text>
      }
      extra={
        sasUrl && (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => downloadFromUrl(sasUrl, fileName)}
          >
            Download
          </Button>
        )
      }
    >
      <div
        style={{
          position: 'relative',
          height: '100%',
          background: VIEWER_BACKDROP,
        }}
      >
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              zIndex: 10,
            }}
          >
            <Space direction="vertical" align="center">
              <Spin size="large" />
              <Text style={{ color: '#fff' }}>Fetching Secure Link...</Text>
            </Space>
          </div>
        )}

        {!sasUrl && !isLoading && (
          <div style={{ padding: 40 }}>
            <Alert
              message="Unable to load this document."
              type="warning"
              showIcon
            />
          </div>
        )}

        <DocumentFrame
          url={sasUrl}
          fileName={fileName}
          contentType={doc.blob?.contentType}
        />
      </div>
    </Drawer>
  );
};

export default TaxRightPanel;
